/**
 * V108-8-19 (owner decision 2026-10-18, "Prompt + baseline bundle") — the "Classify <system>" step, in place.
 *
 * A system with no obligations (never classified, which every `gac init` registration is, or answers that match no
 * rule) has its CI gate run a signed baseline policy with no AI-duty checks. This opens the classification questions
 * where the person already is: inline on the Setup row and onboarding's "Do this next" card, and in a drawer from
 * Today's Next step strip (the strip stays one row: glance review). The questions start from what the workspace
 * already knows (`progress.gap.prefill`: stored answers, the workspace's markets, the system's internet reachability
 * or discovery's suggestion); nothing is guessed (oversight and decision types start unanswered unless known).
 * Saving goes through the existing `PATCH /api/w/:slug/systems/:id/classification` (owner / admin; the server
 * enforces it), which computes the obligations. Anyone else gets Ask an admin. Answers that match no rule say so,
 * and the step keeps an action (Skip is the row's own): never a dead end; the CI gate keeps running meanwhile.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Button, Drawer, Field, Select } from '@aigovops/design-system';
import { SCOPE_DATA_CLASSES, type DecisionType, type ScopeDataClass, type SetupGapProgress } from '@aigovops/core';
import { updateSystemClassification } from '../../lib/api';
import { useInvalidate } from '../../lib/query-keys';
import { toUseCaseVocabulary } from '../../lib/onboarding-model';
import { AskAdmin } from '../AskAdmin';
import { AFFECTED_PERSON_OPTIONS } from '../SystemIntakeFields';
// V112-3-10 (ADR-0099 §1): the one market list (core MARKETS, all 10) the scope screens use.
import { MARKET_OPTIONS } from '../scope/scope-options';

type ClassifyGap = Extract<SetupGapProgress, { kind: 'system_classify' }>;

/** Plain-words labels (aesthetic review High: no terms of art); the values are core's scope vocabulary (SCOPE_DATA_CLASSES). */
const DATA_OPTIONS: ReadonlyArray<{ value: ScopeDataClass; label: string }> = [
  { value: 'pii', label: 'Personal data' }, { value: 'phi', label: 'Health data' }, { value: 'financial', label: 'Financial data' },
  { value: 'proprietary', label: 'Confidential business data' }, { value: 'public', label: 'Public data only' },
  { value: 'cui', label: 'US government controlled data' }, { value: 'de_identified', label: 'De-identified data' },
];
const OVERSIGHT_OPTIONS = [
  { value: 'human_in_the_loop', label: 'A person approves each result' }, { value: 'human_on_the_loop', label: 'A person watches and can step in' },
  { value: 'decision_support', label: 'It suggests, a person decides' }, { value: 'informational', label: 'Information only' },
  { value: 'fully_automated', label: 'Fully automatic, no person involved' },
];
/** Core DECISION_TYPES (ADR-0099 amendment: `other` = a decision about a person outside the three named kinds). */
const DECISIONS: ReadonlyArray<{ value: DecisionType; label: string }> = [
  { value: 'employment', label: 'Hiring or work' }, { value: 'healthcare', label: 'Health care' }, { value: 'credit', label: 'Credit or lending' },
  { value: 'other', label: 'Something else about a person (housing, education, insurance…)' }, { value: 'none', label: 'Nothing about a person' },
];
/** Stored rule vocabulary → the intake options (`workers` is asked as Employees). */
const FROM_RULE_PEOPLE: Record<string, string> = { workers: 'employees' };

export interface ClassifyAnswers { markets: string[]; people: string[]; data: string[]; decisions: string[]; autonomy: string; internet: '' | 'yes' | 'no' }

export function initialAnswers(g: ClassifyGap): ClassifyAnswers {
  const p = g.prefill;
  const people = [...new Set(p.affected_persons.map((x) => FROM_RULE_PEOPLE[x] ?? x))].filter((x) => AFFECTED_PERSON_OPTIONS.some((o) => o.value === x));
  const known = p.decisions.filter((d) => DECISIONS.some((o) => o.value === d));
  return {
    markets: p.markets.filter((m) => MARKET_OPTIONS.some((o) => o.value === m)),
    people,
    data: p.data_classes.filter((d) => DATA_OPTIONS.some((o) => o.value === d)),
    // Earlier answers said "consequential" without a type: never invent one (flow review High); none when answered no.
    decisions: known.length ? known : g.prefill.from.answers && !p.consequential ? ['none'] : [],
    // Unknown oversight starts unanswered (flow review High): it drives obligations, so it is never a silent default.
    autonomy: p.autonomy_level && OVERSIGHT_OPTIONS.some((o) => o.value === p.autonomy_level) ? p.autonomy_level : '',
    internet: p.internet === null ? '' : p.internet ? 'yes' : 'no',
  };
}

/** The PATCH body (the fields the obligation rules read). */
export function classificationBody(a: ClassifyAnswers): Parameters<typeof updateSystemClassification>[2] {
  const vocab = toUseCaseVocabulary({ dataClasses: a.data, affectedPersons: a.people });
  // ADR-0099 §1: every scope data class reaches the engine (the old shim dropped cui, de_identified, proprietary, public).
  const dataClasses = a.data.filter((d) => (SCOPE_DATA_CLASSES as readonly string[]).includes(d));
  const decides = a.decisions.filter((d) => d !== 'none');
  return {
    deployment_jurisdictions: a.markets,
    affected_person_categories: a.people,
    affected_persons: vocab.affected_persons,
    data_classes: dataClasses,
    autonomy_level: a.autonomy,
    consequential_decision: decides.length > 0,
    employment_decision: decides.includes('employment'),
    healthcare_decision: decides.includes('healthcare'),
    credit_decision: decides.includes('credit'),
    ...(a.internet ? { exposure_level: a.internet === 'yes' ? 'public' : 'internal' } : {}),
  };
}

/** Where the starting values came from, in a few words (never vendor terms). */
function prefilledFrom(g: ClassifyGap): string | null {
  const f = g.prefill.from;
  const parts = [
    f.answers ? 'your earlier answers' : null,
    f.markets === 'profile' ? 'your workspace markets' : null,
    f.internet === 'discovery' ? 'discovery' : f.internet === 'system' ? "the system's settings" : null,
  ].filter(Boolean);
  return parts.length ? `Prefilled from ${parts.join(' and ')}.` : null;
}

/** A save failure in plain words, with what to do (never raw server text). */
function saveError(e: unknown): { text: string; forbidden: boolean } {
  const status = (e as { status?: number } | null)?.status;
  const msg = e instanceof Error ? e.message.toLowerCase() : '';
  if (status === 403 || msg.includes('forbidden')) return { text: 'Only an owner or admin can save these answers.', forbidden: true };
  if (status === 422 || msg.includes('validation')) return { text: 'Some answers were not accepted. Check them and save again.', forbidden: false };
  return { text: 'That did not save. Try again.', forbidden: false };
}

type FieldKey = 'markets' | 'decisions' | 'autonomy';

function Checks({ legend, name, options, selected, onChange, required, errorId, exclusive }: {
  legend: string; name: string; options: ReadonlyArray<{ value: string; label: string }>; selected: string[]; onChange: (next: string[]) => void;
  required?: boolean; errorId?: string | null; exclusive?: string;
}) {
  const toggle = (v: string, on: boolean) => {
    if (!on) return onChange(selected.filter((x) => x !== v));
    if (exclusive && v === exclusive) return onChange([v]);
    return onChange([...selected.filter((x) => x !== exclusive), v]);
  };
  return <fieldset aria-describedby={errorId ?? undefined}>
    <legend>{legend}{required ? <span className="su-hint"> (required)</span> : null}</legend>
    {options.map((o) => <label key={o.value} className="su-radio"><input type="checkbox" name={name} value={o.value} checked={selected.includes(o.value)}
      aria-invalid={errorId ? true : undefined} onChange={(e) => toggle(o.value, e.target.checked)} />{o.label}</label>)}
  </fieldset>;
}

function ClassifyForm({ gap, slug, primary, headingLevel, onDone, onCancel, announce, draft, setDraft }: {
  gap: ClassifyGap; slug: string; primary: boolean; headingLevel: 'h3' | 'h4' | 'h5';
  onDone: (count: number) => void; onCancel: () => void; announce: (m: string) => void;
  draft: ClassifyAnswers; setDraft: (a: ClassifyAnswers) => void;
}) {
  const uid = useId();
  const a = draft;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ text: string; forbidden: boolean } | null>(null);
  const [missing, setMissing] = useState<FieldKey[]>([]);
  const invalidate = useInvalidate();
  const set = (patch: Partial<ClassifyAnswers>) => { setDraft({ ...a, ...patch }); setMissing((m) => m.filter((k) => !(k in patch))); };
  const from = prefilledFrom(gap);
  const formRef = useRef<HTMLFormElement>(null);
  // A11y: opening the questions moves focus to the first one (never left on a button that is gone).
  useEffect(() => { formRef.current?.querySelector<HTMLInputElement>('input')?.focus(); }, []);
  const errId = (k: FieldKey) => (missing.includes(k) ? `${uid}-${k}-err` : null);
  const save = async () => {
    if (busy) return;
    const gaps: FieldKey[] = [...(!a.markets.length ? ['markets' as const] : []), ...(!a.decisions.length ? ['decisions' as const] : []), ...(!a.autonomy ? ['autonomy' as const] : [])];
    if (gaps.length) {
      setMissing(gaps);
      window.setTimeout(() => formRef.current?.querySelector<HTMLElement>(`[data-q="${gaps[0]}"] input, [data-q="${gaps[0]}"] select`)?.focus(), 0);
      return;
    }
    setBusy(true); setErr(null);
    announce('Saving…');
    try {
      const r = await updateSystemClassification(slug, gap.system_id, classificationBody(a));
      invalidate('classification:changed', slug);
      const count = r.obligations?.count ?? 0;
      announce(count > 0 ? `${gap.name} is classified: ${count} obligation${count === 1 ? '' : 's'}. Its next CI run checks them.` : 'Saved. Your answers match no rule we track yet.');
      onDone(count);
    } catch (e) {
      setErr(saveError(e));
    } finally { setBusy(false); }
  };
  const Heading = headingLevel;
  const errLine = (k: FieldKey, text: string) => missing.includes(k) ? <p id={`${uid}-${k}-err`} className="su-err">{text}</p> : null;
  return <form ref={formRef} className="su-inline-form su-classify" aria-labelledby={`${uid}-h`} aria-describedby={from ? `${uid}-from` : undefined} data-part="classify-form" aria-busy={busy || undefined} noValidate
    onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } }}
    onSubmit={(e) => { e.preventDefault(); void save(); }}>
    <Heading id={`${uid}-h`} className="su-form-title">{`How ${gap.name} is used`}</Heading>
    {from && <p className="su-hint" id={`${uid}-from`} data-part="classify-from">{from}</p>}
    {missing.length > 0 && <p className="su-err" data-part="classify-missing">{`Answer ${missing.length === 1 ? 'the question' : `the ${missing.length} questions`} marked below.`}</p>}
    <div data-q="markets"><Checks legend="Where is it used?" name={`${uid}-m`} options={MARKET_OPTIONS} selected={a.markets} required errorId={errId('markets')} onChange={(markets) => set({ markets })} />
      {errLine('markets', 'Pick at least one place it is used.')}</div>
    <Checks legend="Who does it affect?" name={`${uid}-p`} options={AFFECTED_PERSON_OPTIONS} selected={a.people} onChange={(people) => set({ people })} />
    <Checks legend="What data does it handle?" name={`${uid}-d`} options={DATA_OPTIONS} selected={a.data} onChange={(data) => set({ data })} />
    <div data-q="decisions"><Checks legend="What does it help decide about a person?" name={`${uid}-c`} options={DECISIONS} selected={a.decisions} required exclusive="none"
      errorId={errId('decisions')} onChange={(decisions) => set({ decisions })} />
      {gap.prefill.consequential && !gap.prefill.decisions.length && !a.decisions.length && <p className="su-hint">Your earlier answers say it helps decide something about people. Pick which.</p>}
      {errLine('decisions', 'Pick what it decides, or "Nothing about a person".')}</div>
    <fieldset>
      <legend>Can people reach it from the internet?</legend>
      {([['yes', 'Yes'], ['no', 'No'], ['', 'Not sure']] as const).map(([v, label]) =>
        <label key={label} className="su-radio"><input type="radio" name={`${uid}-i`} checked={a.internet === v} onChange={() => set({ internet: v })} />{label}</label>)}
    </fieldset>
    <div data-q="autonomy">
      <Field id={`${uid}-o`} label="How much does a person check it? (required)" error={missing.includes('autonomy') ? 'Pick how much a person checks it.' : undefined}>
        <Select id={`${uid}-o`} value={a.autonomy} onChange={(e) => set({ autonomy: e.target.value })}>
          <option value="">Choose one</option>
          {OVERSIGHT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </Field>
    </div>
    <div className="su-acts">
      <Button type="submit" variant={primary ? 'primary' : 'secondary'} aria-disabled={busy || undefined} aria-busy={busy || undefined}>{busy ? 'Saving…' : 'Save answers'}</Button>
      <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
    {err && <div role="alert" className="su-acts"><p className="su-err">{err.text}</p>
      {err.forbidden && <AskAdmin slug={slug} compact what="classifies systems" subject={`GAC Pro: please classify ${gap.name}`} />}</div>}
  </form>;
}

/**
 * The step's actions. `primary`: the opener (and Save) is the surface's one filled primary (Today's strip / onboarding's
 * card decide). `mode`: `inline` (Setup row, onboarding card) or `drawer` (Today's strip: the strip stays one row).
 * `headingLevel`: the form title's level under the host's step title.
 */
export function ClassifyActions({ gap, slug, approver, onChanged, announce, primary = false, mode = 'inline', headingLevel = 'h5', showWhy = true, afterSave }: {
  gap: ClassifyGap; slug: string; approver: boolean; onChanged: () => Promise<unknown>; announce: (m: string) => void;
  primary?: boolean; mode?: 'inline' | 'drawer'; headingLevel?: 'h3' | 'h4' | 'h5'; showWhy?: boolean;
  /** A11y review High: where focus goes when the refresh removed this step (a stable heading of the host). */
  afterSave?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [noMatch, setNoMatch] = useState(false);
  const [saved, setSaved] = useState<number | null>(null);
  const [draft, setDraft] = useState<ClassifyAnswers>(() => initialAnswers(gap));
  const opener = useRef<HTMLButtonElement>(null);
  const doneRef = useRef<HTMLParagraphElement>(null);
  const unmatched = noMatch || gap.reason === 'no_rule_matched';
  const focusOpener = () => window.setTimeout(() => opener.current?.focus(), 0);
  const why = showWhy ? <p className="su-hint" data-part="classify-why">{unmatched
    ? 'Your answers match no rule we track yet. Its CI gate still runs, without AI-duty checks.'
    : 'Until it is classified, its CI gate runs without AI-duty checks.'}</p> : null;
  if (!approver) {
    return <div data-part="gap-actions">{why}<div className="su-acts"><AskAdmin slug={slug} compact what="classifies systems" subject={`GAC Pro: please classify ${gap.name}`} primary={primary} /></div></div>;
  }
  // Flow review M: what was saved stays visible until the refresh closes the step (no opener inviting a second save).
  if (saved !== null && saved > 0) {
    return <div data-part="gap-actions"><p className="su-st-line" data-part="classify-done" tabIndex={-1} ref={doneRef}>
      <strong>{`Classified · ${saved} obligation${saved === 1 ? '' : 's'}.`}</strong><span>Its next CI run checks them.</span></p></div>;
  }
  const close = () => { setOpen(false); focusOpener(); };
  const done = (count: number) => {
    setOpen(false);
    if (count > 0) { setSaved(count); window.setTimeout(() => doneRef.current?.focus(), 0); }
    else { setNoMatch(true); focusOpener(); }
    // After the refresh: if this step is gone (or its focus target is), land on the host's stable target, never <body>.
    void onChanged().then(() => window.setTimeout(() => {
      const target = count > 0 ? doneRef.current : opener.current;
      if (!target?.isConnected && afterSave) afterSave();
    }, 0));
  };
  const form = <ClassifyForm gap={gap} slug={slug} primary={primary || mode === 'drawer'} headingLevel={mode === 'drawer' ? 'h3' : headingLevel}
    announce={announce} onCancel={close} onDone={done} draft={draft} setDraft={setDraft} />;
  if (open && mode === 'inline') return <div data-part="gap-actions">{form}</div>;
  return <div data-part="gap-actions">{why}<div className="su-acts">
    <Button ref={opener} variant={primary ? 'primary' : undefined} onClick={() => setOpen(true)}>
      {unmatched ? 'Review the answers' : 'Answer 6 questions'}<span className="su-sr">{`: ${gap.name}`}</span>
    </Button>
  </div>
  {mode === 'drawer' && <Drawer modal open={open} onClose={close} label={`Classify ${gap.name}`} returnFocus={() => opener.current}>{form}</Drawer>}
  </div>;
}
